import { api } from "./client";
import type {
  CatalogStats,
  Category,
  Order,
  OrderType,
  PriceChange,
  Product,
  Receipt,
  ReservationType,
  Role,
  Shift,
  Subscription,
  User,
} from "../types";

// Текущий пользователь (роль + профиль).
export const me = () => api<User>("/me");

// Публичная конфигурация (username бота, доступность онлайн-оплаты, демо-вход).
export const getConfig = () =>
  api<{
    bot_username: string;
    payments_enabled: boolean;
    demo_login: boolean;
    support_url: string;
  }>("/config");

// Демо-вход на сайте под выбранной ролью (доступен при ALLOW_DEMO_LOGIN).
// Ставит cookie-сессию; дальше api() ходит с credentials: include.
export const demoLogin = (role: Role) =>
  api<User>("/auth/demo-login", { method: "POST", body: JSON.stringify({ role }) });

// Завершить веб-сессию.
export const logout = () => api<{ ok: boolean }>("/auth/logout", { method: "POST" });

// Список товаров с опциональной фильтрацией.
export const listProducts = (params: {
  category?: Category;
  size?: string;
  price_min?: number;
  price_max?: number;
  q?: string;
  only_available?: boolean;
  show_sold?: boolean;
  new_only?: boolean;
  my_size?: boolean;
  seed?: string;
  limit?: number;
  offset?: number;
}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") q.set(k, String(v));
  });
  return api<Product[]>(`/products?${q.toString()}`);
};

// Сводка каталога для главной: цифры в шапке и плитки категорий одним запросом.
export const catalogStats = () => api<CatalogStats>("/products/stats");

// Один товар по ID.
export const getProduct = (id: number) => api<Product>(`/products/${id}`);

// Похожие товары (для блока «вам может понравиться»).
export const similarProducts = (id: number) => api<Product[]>(`/products/${id}/similar`);

// Товары по списку ID (для «недавно просмотренных»). Порядок выстраивает фронт.
export const productsByIds = (ids: number[]) =>
  ids.length ? api<Product[]>(`/products?ids=${ids.join(",")}`) : Promise.resolve([] as Product[]);

// Засчитать просмотр карточки (инкремент счётчика; дедуп — на клиенте).
export const trackView = (id: number) =>
  api<{ views: number }>(`/products/${id}/view`, { method: "POST" });

// Обновление товара менеджером.
export const updateProduct = (
  id: number,
  payload: Partial<Pick<Product, "title" | "category" | "size" | "price" | "condition" | "status">>,
) => api<Product>(`/products/${id}`, { method: "PATCH", body: JSON.stringify(payload) });

/**
 * Возвращает URL прокси-фото товара.
 * Используется если нужен прямой src (например, img tag без fetch-обёртки).
 * Для отображения с авторизацией используй компонент ProductPhoto.
 */
export const photoUrl = (productId: number) => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";
  return `${base}/products/${productId}/photo`;
};

// Создание заказа (покупка или бронь).
export const createOrder = (
  product_id: number,
  type: OrderType,
  reservation_type?: ReservationType,
) =>
  api<Order>("/orders", {
    method: "POST",
    body: JSON.stringify({ product_id, type, reservation_type }),
  });

// Заказы текущего пользователя.
export const myOrders = () => api<Order[]>("/orders/mine");

// Поиск заказов клиента по username или телефону (для менеджера).
export const findOrders = (query: string) =>
  api<Order[]>("/orders/find", {
    method: "POST",
    body: JSON.stringify({ query }),
  });

// Подтверждение выдачи товара менеджером. `payment_method` — чем расплатились
// при получении («cash» / «transfer»); у оплаченных онлайн не спрашивается.
export const issueOrder = (orderId: number, payment_method?: "cash" | "transfer") =>
  api<Order>(`/orders/${orderId}/issue`, {
    method: "POST",
    body: JSON.stringify({ payment_method: payment_method ?? null }),
  });

// Отмена брони менеджером — товар возвращается в каталог.
export const cancelOrder = (orderId: number) =>
  api<Order>(`/orders/${orderId}/cancel`, { method: "POST" });

// Все активные заказы для менеджера.
export const pendingOrders = () => api<Order[]>("/orders/pending");

// Архив завершённых заказов (выданные, истёкшие, снятые) — чеки и талоны
// остаются доступны менеджеру после того, как заказ ушёл из активных.
export const archiveOrders = (limit = 50, offset = 0) =>
  api<Order[]>(`/orders/archive?limit=${limit}&offset=${offset}`);

// Управление сменами.
export const openShift = () => api<Shift>("/shifts/open", { method: "POST" });
export const closeShift = () => api<Shift>("/shifts/close", { method: "POST" });
export const currentShift = () => api<Shift | null>("/shifts/current");

// Подписки на уведомления о новинках.
export const listSubscriptions = () =>
  api<Subscription[]>("/subscriptions");

export const createSubscription = (payload: {
  category?: Category;
  size?: string;
  price_max?: number;
}) => api<Subscription>("/subscriptions", { method: "POST", body: JSON.stringify(payload) });

export const deleteSubscription = (id: number) =>
  api<void>(`/subscriptions/${id}`, { method: "DELETE" });

// Создать invoice link для оплаты (товар/залог) через Telegram Payments.
export const createInvoiceLink = (orderId: number) =>
  api<{ link: string }>(`/payments/invoice/${orderId}`, { method: "POST" });

// ДЕМО-оплата (когда платёжный провайдер не настроен): помечает оплату, возвращает чек.
export const demoPay = (orderId: number) =>
  api<Receipt>(`/payments/demo-pay/${orderId}`, { method: "POST" });

// Чек по оплаченному заказу (клиент или менеджер).
export const getReceipt = (orderId: number) =>
  api<Receipt>(`/payments/receipt/${orderId}`);

// Admin: список всех пользователей.
export const adminListUsers = () => api<User[]>("/admin/users");

// Admin: изменить роль пользователя.
export const adminSetRole = (userId: number, role: string) =>
  api<User>(`/admin/users/${userId}/role`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });

// Admin: журнал чувствительных действий.
export interface AuditEntry {
  id: number;
  actor_label: string;
  action: string;
  target: string | null;
  detail: string | null;
  created_at: string;
}
export const adminAudit = () => api<AuditEntry[]>("/admin/audit");

// Admin: назначить роль по username.
export const adminSetRoleByUsername = (username: string, role: string) =>
  api<User>("/admin/users/by-username", {
    method: "PATCH",
    body: JSON.stringify({ username, role }),
  });

// Лайки: список ID лайкнутых товаров и полные карточки.
export const myLikedIds = () => api<number[]>("/me/liked-ids");
export const myLikedProducts = () => api<Product[]>("/me/liked-products");
export const toggleLike = (productId: number) =>
  api<{ liked: boolean }>(`/products/${productId}/like`, { method: "POST" });

// Проданные товары за период.
export const soldOrders = (year: number, month: number) =>
  api<Order[]>(`/analytics/sold?year=${year}&month=${month}`);

// Активные брони.
export const activeReservations = () => api<Order[]>("/analytics/reservations");

// История изменений цен за период.
export const priceChanges = (year: number, month: number) =>
  api<PriceChange[]>(`/analytics/price-changes?year=${year}&month=${month}`);

// Воронка конверсии по каталогу (накопительно).
export const conversionFunnel = () =>
  api<{
    views: number;
    likes: number;
    reservations: number;
    purchases: number;
    cr_like: number;
    cr_reservation: number;
    cr_purchase: number;
    top_viewed_not_sold: {
      id: number; sku: string; title: string | null;
      category: string; price: number; views: number; likes: number;
    }[];
    by_category: { category: string; views: number; likes: number; sold: number }[];
  }>("/analytics/funnel");

// Месячная аналитика для владельца.
export const monthlyReport = (year: number, month: number) =>
  api<{
    period: string;
    sales_count: number;
    revenue: number;
    reservations_count: number;
    reservations_burned: number;
    deposits_burned_revenue: number;
    products_added: number;
    top_categories: { category: string; count: number }[];
    avg_check: number;
    reservation_conversion: number;
    stale_count: number;
    stale_days: number;
  }>(`/analytics/monthly?year=${year}&month=${month}`);
